import React, { useState, useEffect, useRef } from 'react';
import { IoSearchOutline } from "react-icons/io5";
import { BaseDocument } from '../../data_models/base';
import TextInputField from './TextInputField';
import { FilledButton } from './Button';
import IconButton from './IconButton';


// Props for your custom SearchBar
type SearchBarProps<T> = {
    searchFunction: (term: string) => Promise<T[] | undefined>;
    renderResult: (item: T) => React.ReactNode;
    placeholder?: string;
    withButton?: boolean;
};

function SearchBar<T extends BaseDocument>({
    searchFunction,
    renderResult,
    placeholder = 'Search...',
    withButton = false,
}: SearchBarProps<T>) {

    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState<T[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    const resultsRef = useRef<HTMLDivElement>(null); // Ref to track the dropdown element


    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (resultsRef.current && !resultsRef.current.contains(event.target as Node)) {
                setResults([]);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    // Optionally trigger search on mount if desired
    useEffect(() => {
        if (!withButton) {
            if (searchTerm.trim().length >= 3) {
                handleSearch();
            } else {
                setResults([]);
            }
        }
    }, [searchTerm]);

    async function handleSearch() {
        setIsSearching(true);
        try {
            const data = await searchFunction(searchTerm)
            if (data) {
                setResults(data);
            }
        } catch (error) {
            console.error('Search error:', error);
            // handle or display errors as needed
        } finally {
            setIsSearching(false);
        }
    }

    // Optionally allow "Enter" key to trigger search
    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            handleSearch();
        }
    }

    return (
        <div className="relative flex flex-col gap-2 w-full max-w-md" ref={resultsRef}>
            {/* Search bar input + icon */}
            <div className="relative flex items-center">
                {/* <IoSearchOutline
                    className="absolute left-2 text-gray-400"
                    size={20}
                /> */}

                <div className='flex-1'>
                    <TextInputField
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                    />
                </div>

                {withButton && <IconButton
                    onClick={handleSearch}
                    icon={<IoSearchOutline />}
                    size='m'
                />
                }
            </div>

            {/* Loading indicator */}
            {(results.length > 0 || isSearching) &&
                <div className={`flex flex-col w-full space-y-1 absolute p-1 mt-12 rounded-lg shadow-lg max-h-56 overflow-auto transition-all duration-200 ease-in-out bg-white border border-neutral-200`}>
                    {isSearching && <p className="caption2">Searching...</p>}
                    {results.map((item) => (
                        <div key={item._id!}>
                            {renderResult(item)}
                        </div>
                    ))}
                </div>
            }

        </div>
    );
}

export default SearchBar;
